/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

// TODO Přejmenovat na PromiseQueue nebo rovnou nahradit za knihovnu?
// - https://github.com/sindresorhus/p-queue
/**
 * Fronta asynchronních akcí.
 * Akce se řadí do fronty a postupně vyvolávájí po obsloužení předchozích akcí (FIFO).
 */
export default class Queue {
  constructor() {
    /**
     * Zřetězená promise, která se skládá z jednotlivých akcí.
     * @private
     * @type {Promise<any>}
     */
    this.promise = Promise.resolve();

    /**
     * {@link AbortController} pro rušení probíhající nebo naplánovaných úloh.
     *
     * Po každém abortu se tvoří nový.
     * @private
     */
    this.controller = new AbortController();
  }

  /**
   * Zařadí novou akci do fronty.
   * @template T
   * @param {(signal: AbortSignal) => (T | PromiseLike<T>)} cb Callback při vyřazení akce.
   *
   * Parametr {@link signal} se může využít pro zrušení provádění vyřazené akce,
   * ale ta vždy skončí s AbortError bez ohledu na to, zda se v reakci na event
   * abort vzápětí vyvolalo resolve/reject. Takže typické použití je např. pro
   * přerušení nekonečné smyčky nebo uvolnění zdrojů než pro ovlivnění výsledku.
   * @returns {Promise<T>} Výsledek callbacku při vyřazení akce.
   * @throws {DOMException & { name: 'AbortError' }} Pokud je probíhající nebo
   * zařazená akce zrušena při vyprázdnění fronty pomocí {@link clear}.
   */
  async enqueue(cb) {
    const { signal } = this.controller;
    const promise =
      // Počká se na výsledek předchozích akcí.
      this.promise
        .catch(() => {})
        // Provede se požadovaná akce.
        .then(() => {
          // Po abortu se callback ani nevyvolá a vyhodí se AbortError.
          // TODO: Zásadně se nesmí použít `signal.throwIfAborted()` (od Chrome
          // 103), ale ani `if (signal.aborted) { throw signal.reason; }`
          // (reason od Chrome 98), protože sice reason v polyfillu
          // abortcontroller-polyfill je, ale throwIfAborted není, a i když se
          // pokoutně patchem přidá, tak se na zařízeních, která mají nějakou
          // nativní implementaci AbortSignal (ale bez této a jiných funkcí),
          // tak se vůbec nepolyfilluje! Bohužel se Queue.enqueue používá pro
          // přijetí obsahu, takže když to zde spadne, tak již nejde vzdáleně
          // nasadit aktualizace!
          if (signal.aborted) {
            // TODO: throw signal.reason;
            throw new DOMException('This operation was aborted', 'AbortError');
          }
          return cb(signal);
        });
    // Aktualizuje se zřetězená promise o novou akci s možností abortu.
    this.promise = new Promise((resolve, reject) => {
      // Abortování promise inspirováno:
      // - https://www.npmjs.com/package/simple-abortable-promise
      // - https://zzdjk6.medium.com/a-simple-implementation-of-abortable-promise-using-abortcontroller-with-typescript-2c163ee050e8
      const abortListener = () => {
        signal.removeEventListener('abort', abortListener);
        // TODO: reject(signal.reason);
        reject(new DOMException('This operation was aborted', 'AbortError'));
      };
      signal.addEventListener('abort', abortListener);
      promise.then(resolve).catch(reject);
      // Nutné ošetřit i novou promise přiřazenou do this.promise, jinak vyskakují Uncaught erros.
    }).catch(() => {});
    // Vrací se výsledek po provedení požadované akce.
    return await promise;
  }

  /**
   * Vyčistí frontu, tj. uvede ji do stavu jako po vytvoření. Případné čekající
   * úlohy zruší s odmítnutím {@link DOMException} s name `AbortError`.
   */
  clear() {
    // AbortController se by design nemůže znovupoužít, takže se musí vytvořit nový.
    this.controller.abort();
    this.controller = new AbortController();
  }
}
